import { BANKALAR, ibanBanka, ibanDenetle, ibanYazarken } from '@artmozaik/shared';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import AraSec, { type AraSecenek } from '../../ui/AraSec';
import Ipucu from '../../ui/Ipucu';
import TutarGiris from '../../ui/TutarGiris';
import {
  PARA_SIMGE, bugunIso, gunYaz, hesapSecenek, ibanGecerli, ibanYaz, paraYaz, yeniAnahtar,
  type DisHesap, type Hareket, type Hesap, type HesapTur, type Kategori, type Para,
} from './ortak';

/**
 * KASA ÇEKMECELERİ — yeni hareket, virman, iptal, hesap tanımı, kategori tanımı.
 * Her seçim "yazdıkça ara" kutusudur (`AraSec`); kısa ve sabit seçenekler (yön, tür,
 * para birimi) düğme öbeğidir. Kategori yoksa çekmeceden çıkmadan tanımlanır.
 */

/* ─── Küçük parçalar ─────────────────────────────────────────────────────── */
function Cekmece({ ust, baslik, kapat, mesgul, children, genis }: {
  ust: string; baslik: string; kapat: () => void; mesgul?: boolean; children: ReactNode; genis?: boolean;
}) {
  return (
    <div className="ccd-drawer-arka" onClick={() => !mesgul && kapat()} role="presentation">
      <aside className={`ccd-drawer${genis ? ' ccd-kasa-cekmece-genis' : ''}`} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={baslik}>
        <div className="ccd-stack">
          <div className="ccd-row-between">
            <div><div className="ccd-eyebrow">{ust}</div><h2 className="ccd-heading">{baslik}</h2></div>
            <button type="button" className="ccd-btn ccd-btn-ghost ccd-btn-sm" onClick={kapat} disabled={mesgul}>Kapat</button>
          </div>
          {children}
        </div>
      </aside>
    </div>
  );
}

const Hata = ({ m }: { m: string | null }) => (m
  ? <div className="ccd-banner ccd-banner-danger" role="alert"><span className="ccd-banner-icon" aria-hidden="true">!</span><span>{m}</span></div> : null);
const Bilgi = ({ m }: { m: string | null }) => (m
  ? <div className="ccd-banner" role="status"><span className="ccd-banner-icon" aria-hidden="true">✓</span><span>{m}</span></div> : null);

/** Kısa ve sabit seçenekler için düğme öbeği (açılır liste yerine). */
export function Secim<T extends string>({ deger, degis, secenekler, etiket, disabled }: {
  deger: T; degis: (d: T) => void; secenekler: readonly (readonly [T, string])[]; etiket: string; disabled?: boolean;
}) {
  return (
    <div className="ccd-kasa-secim" role="group" aria-label={etiket}>
      {secenekler.map(([id, ad]) => (
        <button key={id} type="button" aria-pressed={deger === id} disabled={disabled} onClick={() => degis(id)}>{ad}</button>
      ))}
    </div>
  );
}

function Alan({ etiket, htmlFor, zorunlu, ipucu, children, not }: {
  etiket: string; htmlFor?: string; zorunlu?: boolean; ipucu?: ReactNode; children: ReactNode; not?: ReactNode;
}) {
  return (
    <div className="ccd-field">
      <label className="ccd-field-label" htmlFor={htmlFor}>{etiket}{zorunlu && <span className="ccd-required"> *</span>} {ipucu}</label>
      {children}
      {not && <div className="ccd-meta">{not}</div>}
    </div>
  );
}

/* ─── Kategori formu (çekmece içinde ve Tanımlar'da aynı) ────────────────── */
type KategoriTaslak = { ad: string; yon: Kategori['yon']; grup: string; emanet: boolean; cariZorunlu: boolean };

function KategoriAlanlari({ t, degis, gruplar, kilitli }: { t: KategoriTaslak; degis: (t: KategoriTaslak) => void; gruplar: string[]; kilitli?: boolean }) {
  return (
    <>
      <Alan etiket="Kategori adı" htmlFor="kasa-kat-ad" zorunlu>
        <input id="kasa-kat-ad" className="ccd-input" maxLength={80} autoFocus value={t.ad} placeholder="Örn. Elektrik faturası"
               onChange={(e) => degis({ ...t, ad: e.target.value })} />
      </Alan>
      <Alan etiket="Grup" htmlFor="kasa-kat-grup" not="Listeden seçin ya da yeni bir grup adı yazın.">
        <input id="kasa-kat-grup" className="ccd-input" maxLength={40} list="kasa-gruplar" value={t.grup} onChange={(e) => degis({ ...t, grup: e.target.value })} />
        <datalist id="kasa-gruplar">{gruplar.map((g) => <option key={g} value={g} />)}</datalist>
      </Alan>
      <Alan etiket="Yön">
        <Secim etiket="Yön" deger={t.yon} degis={(yon) => degis({ ...t, yon })} disabled={kilitli}
               secenekler={[['GIRIS', 'Giriş'], ['CIKIS', 'Çıkış'], ['IKISI', 'İkisi de']] as const} />
      </Alan>
      <label className="ccd-kasa-onay">
        <input type="checkbox" checked={t.emanet} disabled={kilitli} onChange={(e) => degis({ ...t, emanet: e.target.checked })} />
        <span><b>Emanet işlemi</b> <Ipucu id="emanet-bakiyesi" /> — bu kategorideki para kişi ya da cari adına emanet bakiyesi oluşturur (kapora, avans, ortak parası).</span>
      </label>
      <label className="ccd-kasa-onay">
        <input type="checkbox" checked={t.cariZorunlu} disabled={kilitli} onChange={(e) => degis({ ...t, cariZorunlu: e.target.checked })} />
        <span><b>Cari seçmek zorunlu</b> — müşteri ya da tedarikçi seçilmeden kaydedilmez.</span>
      </label>
    </>
  );
}

/** Tanımlar sekmesindeki "kategori ekle / düzenle" çekmecesi. */
export function KategoriCekmece({ kategori, gruplar, kapat, kaydedildi }: {
  kategori: Kategori | null; gruplar: string[]; kapat: () => void; kaydedildi: (k: Kategori, mesaj: string) => void;
}) {
  const [t, setT] = useState<KategoriTaslak>(kategori
    ? { ad: kategori.ad, yon: kategori.yon, grup: kategori.grup, emanet: kategori.emanet, cariZorunlu: kategori.cariZorunlu }
    : { ad: '', yon: 'CIKIS', grup: 'Diğer', emanet: false, cariZorunlu: false });
  const [hata, setHata] = useState<string | null>(null);
  const [bekliyor, setBekliyor] = useState(false);
  const kilitli = Boolean(kategori?.kod);

  async function kaydet(e: React.FormEvent) {
    e.preventDefault();
    if (t.ad.trim().length < 2) { setHata('Kategori adını yazın.'); return; }
    setHata(null); setBekliyor(true);
    try {
      const govde = { ...t, ad: t.ad.trim(), grup: t.grup.trim() || 'Diğer' };
      const r = kategori
        ? await api<{ kategori: Kategori }>(`/api/kasa/kategori/${kategori.id}`, { method: 'PUT', body: JSON.stringify(govde) })
        : await api<{ kategori: Kategori }>('/api/kasa/kategori', { method: 'POST', body: JSON.stringify(govde) });
      kaydedildi(r.kategori, `"${r.kategori.ad}" kaydedildi.`);
    } catch (err) { setHata(err instanceof Error ? err.message : 'Kategori kaydedilemedi'); }
    finally { setBekliyor(false); }
  }

  return (
    <Cekmece ust="Tanımlar" baslik={kategori ? 'İşlem kategorisini düzenle' : 'Yeni işlem kategorisi'} kapat={kapat} mesgul={bekliyor}>
      <form className="ccd-stack" onSubmit={kaydet}>
        {kilitli && <div className="ccd-meta">Bu kategoriyi panel kendi yazdığı kayıtlarda kullanır: adı ve grubu değişir; yönü ve emanet işareti değişmez.</div>}
        {kategori && !kilitli && kategori.kullanim > 0 && <div className="ccd-meta">Bu kategoride {kategori.kullanim} hareket var. Emanet işareti artık değiştirilemez; gerekirse yeni bir kategori açıp bunu pasife alın.</div>}
        <Hata m={hata} />
        <KategoriAlanlari t={t} degis={setT} gruplar={gruplar} kilitli={kilitli} />
        <button className="ccd-btn ccd-btn-primary ccd-btn-lg" disabled={bekliyor}>{bekliyor ? 'Kaydediliyor…' : 'Kaydet'}</button>
      </form>
    </Cekmece>
  );
}

/* ─── Yeni hareket ───────────────────────────────────────────────────────── */
export type HareketOn = { hesapId?: number; yon?: 'GIRIS' | 'CIKIS'; kategoriId?: number; customerId?: number; supplierId?: number; kisi?: string };
type Secenekler = { musteriler: AraSecenek[]; tedarikciler: AraSecenek[]; kisiler: string[] };
type Kime = 'YOK' | 'MUSTERI' | 'TEDARIKCI' | 'KISI';

export function HareketCekmece({ hesaplar, kategoriler, gruplar, on, kapat, kaydedildi, tanimYenile, hesapTanimla }: {
  hesaplar: Hesap[]; kategoriler: Kategori[]; gruplar: string[]; on?: HareketOn;
  kapat: () => void;
  /** Her başarılı kayıttan sonra (çekmece açık kalsa da) çağrılır. */
  kaydedildi: (mesaj: string) => void;
  /** Çekmeceden yeni kategori tanımlanınca tanımlar yeniden okunur. */
  tanimYenile: () => Promise<void>;
  /** Hesap yoksa "hesap tanımla"ya yönlendirir. */
  hesapTanimla?: () => void;
}) {
  const { can } = useAuth();
  const etkin = useMemo(() => hesaplar.filter((h) => h.aktif), [hesaplar]);
  const [yon, setYon] = useState<'GIRIS' | 'CIKIS'>(on?.yon ?? 'GIRIS');
  const [hesapId, setHesapId] = useState(String(on?.hesapId ?? (etkin.length === 1 ? etkin[0].id : '')));
  const [tarih, setTarih] = useState(bugunIso());
  const [kurus, setKurus] = useState<number | null>(null);
  const [kategoriId, setKategoriId] = useState(String(on?.kategoriId ?? ''));
  const [kime, setKime] = useState<Kime>(on?.customerId ? 'MUSTERI' : on?.supplierId ? 'TEDARIKCI' : on?.kisi ? 'KISI' : 'YOK');
  const [customerId, setCustomerId] = useState(String(on?.customerId ?? ''));
  const [supplierId, setSupplierId] = useState(String(on?.supplierId ?? ''));
  const [kisi, setKisi] = useState(on?.kisi ?? '');
  const [aciklama, setAciklama] = useState('');
  const [belgeNo, setBelgeNo] = useState('');
  const [vade, setVade] = useState('');
  const [hata, setHata] = useState<string | null>(null);
  const [bilgi, setBilgi] = useState<string | null>(null);
  const [bekliyor, setBekliyor] = useState(false);
  const [sec, setSec] = useState<Secenekler | null>(null);
  const [yeniKat, setYeniKat] = useState<KategoriTaslak | null>(null);
  const [katBekliyor, setKatBekliyor] = useState(false);
  /** Aynı form iki kez gönderilirse sunucu tek kayıt yazar; başarılı kayıttan sonra yenilenir. */
  const anahtar = useRef(yeniAnahtar());

  useEffect(() => { api<Secenekler>('/api/kasa/secenekler').then(setSec).catch(() => setSec({ musteriler: [], tedarikciler: [], kisiler: [] })); }, []);

  const hesap = etkin.find((h) => String(h.id) === hesapId) ?? null;
  const uygun = useMemo(() => kategoriler.filter((k) => k.aktif && k.kod !== 'VIRMAN' && (k.yon === 'IKISI' || k.yon === yon)), [kategoriler, yon]);
  const kategori = uygun.find((k) => String(k.id) === kategoriId) ?? null;
  // Yön değişince seçili kategori o yöne uymuyorsa bırakılır.
  useEffect(() => { if (kategoriId && !uygun.some((k) => String(k.id) === kategoriId)) setKategoriId(''); }, [uygun, kategoriId]);
  // Emanet kategorisinde "kime" boşsa kişi alanı açılır.
  useEffect(() => { if (kategori?.emanet && kime === 'YOK') setKime('KISI'); }, [kategori, kime]);

  async function kategoriEkle() {
    if (!yeniKat) return;
    if (yeniKat.ad.trim().length < 2) { setHata('Kategori adını yazın.'); return; }
    setHata(null); setKatBekliyor(true);
    try {
      const r = await api<{ kategori: Kategori }>('/api/kasa/kategori', { method: 'POST', body: JSON.stringify({ ...yeniKat, ad: yeniKat.ad.trim(), grup: yeniKat.grup.trim() || 'Diğer' }) });
      await tanimYenile();
      setKategoriId(String(r.kategori.id)); setYeniKat(null);
    } catch (e) { setHata(e instanceof Error ? e.message : 'Kategori kaydedilemedi'); }
    finally { setKatBekliyor(false); }
  }

  async function kaydet(acikKalsin: boolean) {
    if (!hesapId) { setHata('Hesap seçin.'); return; }
    if (kurus == null || kurus <= 0) { setHata('Tutarı yazın.'); return; }
    if (!kategoriId) { setHata('İşlem kategorisi seçin.'); return; }
    if (kategori?.emanet && !(kime === 'MUSTERI' && customerId) && !(kime === 'TEDARIKCI' && supplierId) && !(kime === 'KISI' && kisi.trim())) {
      setHata('Bu bir emanet işlemi: kimin emaneti olduğunu seçin ya da yazın.'); return;
    }
    setHata(null); setBilgi(null); setBekliyor(true);
    try {
      const r = await api<{ id: number; bakiyeKurus: number; eksiyeDustu: boolean; hesapAd: string }>('/api/kasa/hareket', {
        method: 'POST',
        body: JSON.stringify({
          hesapId: Number(hesapId), tarih, yon, tutarKurus: kurus, kategoriId: Number(kategoriId),
          customerId: kime === 'MUSTERI' && customerId ? Number(customerId) : null,
          supplierId: kime === 'TEDARIKCI' && supplierId ? Number(supplierId) : null,
          kisi: kime === 'KISI' ? kisi.trim() || null : null,
          aciklama: aciklama.trim() || null, belgeNo: belgeNo.trim() || null, vadeTarihi: vade || null,
          islemAnahtari: anahtar.current,
        }),
      });
      anahtar.current = yeniAnahtar();
      const mesaj = `${yon === 'GIRIS' ? 'Giriş' : 'Çıkış'} kaydedildi: ${paraYaz(kurus, hesap?.para)} · ${r.hesapAd} bakiyesi ${paraYaz(r.bakiyeKurus, hesap?.para)}`
        + (r.eksiyeDustu ? ' — DİKKAT: hesap eksiye düştü.' : '');
      kaydedildi(mesaj);
      if (acikKalsin) { setBilgi(mesaj); setKurus(null); setAciklama(''); setBelgeNo(''); setVade(''); }
      else kapat();
    } catch (e) { setHata(e instanceof Error ? e.message : 'Hareket kaydedilemedi'); }
    finally { setBekliyor(false); }
  }

  if (etkin.length === 0) {
    return (
      <Cekmece ust="Kasa ve Banka" baslik="Yeni hareket" kapat={kapat}>
        <div className="ccd-empty">
          Hareket girmek için önce bir kasa ya da banka hesabı tanımlayın.
          {hesapTanimla && <div style={{ marginTop: 12 }}><button type="button" className="ccd-btn ccd-btn-primary" onClick={hesapTanimla}>＋ Hesap tanımla</button></div>}
        </div>
      </Cekmece>
    );
  }

  return (
    <Cekmece ust="Kasa ve Banka" baslik="Yeni hareket" kapat={kapat} mesgul={bekliyor}>
      <form className="ccd-stack" onSubmit={(e) => { e.preventDefault(); void kaydet(false); }}>
        <Hata m={hata} />
        <Bilgi m={bilgi} />
        <Secim etiket="Giriş mi çıkış mı" deger={yon} degis={setYon} secenekler={[['GIRIS', '↓ Para girdi'], ['CIKIS', '↑ Para çıktı']] as const} />
        <Alan etiket="Hesap" htmlFor="kasa-h-hesap" zorunlu ipucu={<Ipucu id="kasa-bakiyesi" />}
              not={hesap ? `Şimdiki bakiye ${paraYaz(hesap.bakiyeKurus, hesap.para)}${hesap.resmi ? '' : ' · gayriresmî hesap'}` : undefined}>
          <AraSec id="kasa-h-hesap" secenekler={etkin.map(hesapSecenek)} deger={hesapId} degis={setHesapId} yerTutucu="Kasa ya da banka adı yazın…" />
        </Alan>
        <div className="ccd-kasa-ikili">
          <Alan etiket="Tutar" htmlFor="kasa-h-tutar" zorunlu>
            <TutarGiris id="kasa-h-tutar" kurus={kurus} degis={setKurus} simge={PARA_SIMGE[hesap?.para ?? 'TRY']} />
          </Alan>
          <Alan etiket="Tarih" htmlFor="kasa-h-tarih" zorunlu>
            <input id="kasa-h-tarih" className="ccd-input" type="date" max={bugunIso()} min={hesap?.acilisTarihi ?? undefined} value={tarih} onChange={(e) => setTarih(e.target.value)} />
          </Alan>
        </div>

        <Alan etiket="İşlem kategorisi" htmlFor="kasa-h-kat" zorunlu ipucu={<Ipucu id="islem-kategorisi" />}
              not={kategori?.emanet ? 'Emanet işlemi: bu para kişi / cari adına emanet bakiyesine yazılır.' : undefined}>
          <AraSec id="kasa-h-kat" secenekler={uygun.map((k) => ({ id: k.id, ad: k.ad, alt: `${k.grup}${k.emanet ? ' · emanet' : ''}` }))}
                  deger={kategoriId} degis={setKategoriId} yerTutucu="Kategori adı yazın: tahsilat, kira, kapora…" azami={30} />
          {!yeniKat && (can('cash.settings')
            ? <button type="button" className="ccd-kasa-bag" onClick={() => setYeniKat({ ad: '', yon, grup: 'Diğer', emanet: false, cariZorunlu: false })}>＋ Yeni kategori</button>
            : <div className="ccd-meta">Listede yoksa yöneticiden tanımlamasını isteyin.</div>)}
        </Alan>
        {yeniKat && (
          <div className="ccd-kasa-icform ccd-stack-sm">
            <div className="ccd-fieldset-title">Yeni işlem kategorisi</div>
            <KategoriAlanlari t={yeniKat} degis={setYeniKat} gruplar={gruplar} />
            <div className="ccd-row" style={{ gap: 8 }}>
              <button type="button" className="ccd-btn ccd-btn-primary ccd-btn-sm" disabled={katBekliyor} onClick={() => void kategoriEkle()}>{katBekliyor ? 'Kaydediliyor…' : 'Kategoriyi kaydet ve seç'}</button>
              <button type="button" className="ccd-btn ccd-btn-ghost ccd-btn-sm" onClick={() => setYeniKat(null)}>Vazgeç</button>
            </div>
          </div>
        )}

        <Alan etiket={yon === 'GIRIS' ? 'Kimden' : 'Kime'} zorunlu={Boolean(kategori?.emanet || kategori?.cariZorunlu)}>
          <Secim etiket="Kimden / kime" deger={kime} degis={setKime}
                 secenekler={[['YOK', 'Belirtme'], ['MUSTERI', 'Müşteri'], ['TEDARIKCI', 'Tedarikçi'], ['KISI', 'Kişi (personel, ortak)']] as const} />
          {kime === 'MUSTERI' && <AraSec secenekler={sec?.musteriler ?? []} deger={customerId} degis={setCustomerId} yukleniyor={!sec} yerTutucu="Müşteri adı yazın…" />}
          {kime === 'TEDARIKCI' && <AraSec secenekler={sec?.tedarikciler ?? []} deger={supplierId} degis={setSupplierId} yukleniyor={!sec} yerTutucu="Tedarikçi adı yazın…" />}
          {kime === 'KISI' && (
            <>
              <input className="ccd-input" maxLength={120} list="kasa-kisiler" value={kisi} placeholder="Ad soyad yazın; daha önce yazılanlar önerilir" onChange={(e) => setKisi(e.target.value)} />
              <datalist id="kasa-kisiler">{(sec?.kisiler ?? []).map((k) => <option key={k} value={k} />)}</datalist>
            </>
          )}
        </Alan>

        <Alan etiket="Açıklama" htmlFor="kasa-h-aciklama">
          <input id="kasa-h-aciklama" className="ccd-input" maxLength={300} value={aciklama} onChange={(e) => setAciklama(e.target.value)} placeholder="İsteğe bağlı" />
        </Alan>
        <div className="ccd-kasa-ikili">
          <Alan etiket="Belge no" htmlFor="kasa-h-belge" not="Makbuz, dekont, çek ya da senet numarası.">
            <input id="kasa-h-belge" className="ccd-input" maxLength={60} value={belgeNo} onChange={(e) => setBelgeNo(e.target.value)} placeholder="İsteğe bağlı" />
          </Alan>
          <Alan etiket="Vade tarihi" htmlFor="kasa-h-vade" not="Yalnız çek ve senette.">
            <input id="kasa-h-vade" className="ccd-input" type="date" min={tarih} value={vade} onChange={(e) => setVade(e.target.value)} />
          </Alan>
        </div>

        <div className="ccd-row" style={{ gap: 8, flexWrap: 'wrap' }}>
          <button className="ccd-btn ccd-btn-primary ccd-btn-lg" disabled={bekliyor}>{bekliyor ? 'Kaydediliyor…' : 'Kaydet'}</button>
          <button type="button" className="ccd-btn ccd-btn-lg" disabled={bekliyor} onClick={() => void kaydet(true)} title="Kaydeder, çekmece açık kalır">Kaydet, yenisini gir</button>
        </div>
        <div className="ccd-meta">Kaydedilen hareket silinmez; yanlışsa listeden iptal edip doğrusunu yeniden girersiniz. <Ipucu id="hareket-iptali" /></div>
      </form>
    </Cekmece>
  );
}

/* ─── Virman ─────────────────────────────────────────────────────────────── */
export function VirmanCekmece({ hesaplar, on, kapat, kaydedildi }: {
  hesaplar: Hesap[]; on?: { kaynakId?: number }; kapat: () => void; kaydedildi: (mesaj: string) => void;
}) {
  const etkin = useMemo(() => hesaplar.filter((h) => h.aktif), [hesaplar]);
  const [kaynakId, setKaynakId] = useState(String(on?.kaynakId ?? ''));
  const [hedefId, setHedefId] = useState('');
  const [tarih, setTarih] = useState(bugunIso());
  const [kurus, setKurus] = useState<number | null>(null);
  const [aciklama, setAciklama] = useState('');
  const [hata, setHata] = useState<string | null>(null);
  const [bekliyor, setBekliyor] = useState(false);
  const anahtar = useRef(yeniAnahtar());
  const kaynak = etkin.find((h) => String(h.id) === kaynakId) ?? null;
  const hedefler = etkin.filter((h) => String(h.id) !== kaynakId && (!kaynak || h.para === kaynak.para));
  const hedef = hedefler.find((h) => String(h.id) === hedefId) ?? null;
  useEffect(() => { if (hedefId && !hedefler.some((h) => String(h.id) === hedefId)) setHedefId(''); }, [hedefler, hedefId]);

  async function kaydet(e: React.FormEvent) {
    e.preventDefault();
    if (!kaynak) { setHata('Paranın çıktığı hesabı seçin.'); return; }
    if (!hedef) { setHata('Paranın girdiği hesabı seçin.'); return; }
    if (kurus == null || kurus <= 0) { setHata('Tutarı yazın.'); return; }
    setHata(null); setBekliyor(true);
    try {
      await api('/api/kasa/virman', {
        method: 'POST',
        body: JSON.stringify({ kaynakId: kaynak.id, hedefId: hedef.id, tarih, tutarKurus: kurus, aciklama: aciklama.trim() || null, islemAnahtari: anahtar.current }),
      });
      kaydedildi(`Virman kaydedildi: ${paraYaz(kurus, kaynak.para)} · ${kaynak.ad} → ${hedef.ad}`);
      kapat();
    } catch (err) { setHata(err instanceof Error ? err.message : 'Virman kaydedilemedi'); }
    finally { setBekliyor(false); }
  }

  return (
    <Cekmece ust="Kasa ve Banka" baslik="Virman (hesaplar arası aktarım)" kapat={kapat} mesgul={bekliyor}>
      <form className="ccd-stack" onSubmit={kaydet}>
        <div className="ccd-meta">Para bir hesabınızdan öbürüne geçer: kasadan bankaya yatırma, bankadan kasaya çekme. Toplam paranız değişmez. <Ipucu id="virman" /></div>
        <Hata m={hata} />
        <Alan etiket="Paranın çıktığı hesap" htmlFor="kasa-v-kaynak" zorunlu not={kaynak ? `Bakiye ${paraYaz(kaynak.bakiyeKurus, kaynak.para)}` : undefined}>
          <AraSec id="kasa-v-kaynak" secenekler={etkin.map(hesapSecenek)} deger={kaynakId} degis={setKaynakId} yerTutucu="Hesap adı yazın…" />
        </Alan>
        <Alan etiket="Paranın girdiği hesap" htmlFor="kasa-v-hedef" zorunlu
              not={kaynak && hedefler.length === 0 ? `${kaynak.para} para biriminde başka etkin hesap yok. Farklı para birimleri arasında virman bu sürümde yapılamıyor.` : hedef ? `Bakiye ${paraYaz(hedef.bakiyeKurus, hedef.para)}` : kaynak ? `Yalnız ${kaynak.para} hesapları listelenir.` : undefined}>
          <AraSec id="kasa-v-hedef" secenekler={hedefler.map(hesapSecenek)} deger={hedefId} degis={setHedefId} disabled={!kaynak} yerTutucu={kaynak ? 'Hesap adı yazın…' : 'Önce çıkış hesabını seçin'} />
        </Alan>
        <div className="ccd-kasa-ikili">
          <Alan etiket="Tutar" htmlFor="kasa-v-tutar" zorunlu>
            <TutarGiris id="kasa-v-tutar" kurus={kurus} degis={setKurus} simge={PARA_SIMGE[kaynak?.para ?? 'TRY']} />
          </Alan>
          <Alan etiket="Tarih" htmlFor="kasa-v-tarih" zorunlu>
            <input id="kasa-v-tarih" className="ccd-input" type="date" max={bugunIso()} value={tarih} onChange={(e) => setTarih(e.target.value)} />
          </Alan>
        </div>
        <Alan etiket="Açıklama" htmlFor="kasa-v-aciklama">
          <input id="kasa-v-aciklama" className="ccd-input" maxLength={300} value={aciklama} onChange={(e) => setAciklama(e.target.value)} placeholder="İsteğe bağlı" />
        </Alan>
        <button className="ccd-btn ccd-btn-primary ccd-btn-lg" disabled={bekliyor}>{bekliyor ? 'Kaydediliyor…' : 'Virmanı kaydet'}</button>
      </form>
    </Cekmece>
  );
}

/* ─── İptal ──────────────────────────────────────────────────────────────── */
export function IptalCekmece({ hareket, kapat, iptalEdildi }: { hareket: Hareket; kapat: () => void; iptalEdildi: (mesaj: string) => void }) {
  const [neden, setNeden] = useState('');
  const [hata, setHata] = useState<string | null>(null);
  const [bekliyor, setBekliyor] = useState(false);

  async function gonder(e: React.FormEvent) {
    e.preventDefault();
    if (neden.trim().length < 3) { setHata('İptal nedenini yazın.'); return; }
    setHata(null); setBekliyor(true);
    try {
      const r = await api<{ idler: number[]; uyari: string | null }>(`/api/kasa/hareket/${hareket.id}/iptal`, { method: 'POST', body: JSON.stringify({ neden: neden.trim() }) });
      iptalEdildi(`Hareket iptal edildi${r.idler.length > 1 ? ' (virmanın iki bacağı birlikte)' : ''}.${r.uyari ? ` ${r.uyari}` : ''}`);
      kapat();
    } catch (err) { setHata(err instanceof Error ? err.message : 'İptal edilemedi'); }
    finally { setBekliyor(false); }
  }

  return (
    <Cekmece ust="Kasa ve Banka" baslik="Hareketi iptal et" kapat={kapat} mesgul={bekliyor}>
      <form className="ccd-stack" onSubmit={gonder}>
        <div className="ccd-kasa-icform">
          <b>{gunYaz(hareket.tarih)} · {hareket.hesapAd}</b>
          <div>{hareket.yon === 'GIRIS' ? 'Giriş' : 'Çıkış'} · <b>{paraYaz(hareket.tutarKurus, hareket.para)}</b> · {hareket.kategoriAd}</div>
          {(hareket.cariAd || hareket.kisi || hareket.aciklama) && <div className="ccd-meta">{[hareket.cariAd ?? hareket.kisi, hareket.aciklama].filter(Boolean).join(' · ')}</div>}
        </div>
        <div className="ccd-meta">
          İptal edilen hareket silinmez: listede üstü çizili kalır, bakiyeye girmez. Doğrusunu yeni hareket olarak girersiniz.
          {hareket.virmanId && ' Bu bir virman: öbür hesaptaki bacağı da birlikte iptal edilir.'} <Ipucu id="hareket-iptali" />
        </div>
        <Hata m={hata} />
        <Alan etiket="İptal nedeni" htmlFor="kasa-iptal-neden" zorunlu>
          <textarea id="kasa-iptal-neden" className="ccd-input" rows={3} maxLength={300} autoFocus value={neden} onChange={(e) => setNeden(e.target.value)} placeholder="Örn. tutar yanlış girildi" />
        </Alan>
        <button className="ccd-btn ccd-btn-danger ccd-btn-lg" disabled={bekliyor}>{bekliyor ? 'İptal ediliyor…' : 'Hareketi iptal et'}</button>
      </form>
    </Cekmece>
  );
}

/* ─── Hesap tanımı ───────────────────────────────────────────────────────── */
export function HesapCekmece({ hesap, gayriGorur, muhasebeAd, muhasebeKip, kapat, kaydedildi }: {
  hesap: Hesap | null; gayriGorur: boolean; muhasebeAd: string; muhasebeKip: 'mock' | 'real' | 'off';
  kapat: () => void; kaydedildi: (mesaj: string) => void;
}) {
  const [ad, setAd] = useState(hesap?.ad ?? '');
  const [tur, setTur] = useState<HesapTur>(hesap?.tur ?? 'KASA');
  const [resmi, setResmi] = useState(hesap?.resmi ?? true);
  const [para, setPara] = useState<Para>(hesap?.para ?? 'TRY');
  const [bankaAdi, setBankaAdi] = useState(hesap?.bankaAdi ?? '');
  const [sube, setSube] = useState(hesap?.sube ?? '');
  const [iban, setIban] = useState(ibanYaz(hesap?.iban ?? null));
  const [hesapNo, setHesapNo] = useState(hesap?.hesapNo ?? '');
  const [hesapSahibi, setHesapSahibi] = useState(hesap?.hesapSahibi ?? '');
  const [acilis, setAcilis] = useState<number | null>(hesap?.acilisKurus ?? 0);
  const [acilisTarihi, setAcilisTarihi] = useState(hesap?.acilisTarihi ?? '');
  const [not, setNot] = useState(hesap?.not ?? '');
  const [aktif, setAktif] = useState(hesap?.aktif ?? true);
  const [disId, setDisId] = useState(hesap?.disHesapId ?? '');
  const [dis, setDis] = useState<DisHesap[] | null>(null);
  const [disHata, setDisHata] = useState<string | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [bekliyor, setBekliyor] = useState(false);
  const bagliProgram = muhasebeKip !== 'off';

  useEffect(() => {
    if (!bagliProgram) return;
    api<{ hesaplar: DisHesap[] }>('/api/kasa/dis-hesaplar').then((r) => setDis(r.hesaplar)).catch((e) => { setDis([]); setDisHata((e as Error).message); });
  }, [bagliProgram]);

  const ibanDurum = iban.trim() ? ibanDenetle(iban) : null;
  const ibanKusurlu = ibanDurum !== null && !ibanDurum.ok;
  const ibanBankasi = ibanBanka(iban);
  /** Banka alanı elle yazılmadıysa (boş ya da önceki IBAN'dan dolduysa) IBAN'daki bankayla doldurulur. */
  function ibanDegis(ham: string, sonda: boolean) {
    const yeni = sonda ? ibanYazarken(ham) : ham.toUpperCase();
    const onceki = ibanBanka(iban)?.ad ?? '';
    const b = ibanBanka(yeni);
    setIban(yeni);
    if (b && (!bankaAdi.trim() || bankaAdi.trim() === onceki)) setBankaAdi(b.ad);
  }
  const bankaUyusmuyor = Boolean(ibanBankasi && bankaAdi.trim() && bankaAdi.trim().toLocaleLowerCase('tr') !== ibanBankasi.ad.toLocaleLowerCase('tr'));
  // Yalnız boşta olan (ya da bu hesaba bağlı) muhasebe hesapları seçilebilir.
  const disSecenek = (dis ?? []).filter((d) => d.bagliHesapId == null || d.bagliHesapId === hesap?.id)
    .map((d) => ({ id: d.disId, ad: d.ad, alt: `${d.tur === 'KASA' ? 'Kasa' : 'Banka'} · ${d.para} · ${paraYaz(d.bakiyeKurus, d.para)}${d.bankaAdi ? ` · ${d.bankaAdi}` : ''}` }));
  const seciliDis = (dis ?? []).find((d) => d.disId === disId) ?? null;

  /** İlişkilendirilen hesaptan boş alanlara öneri: ad, tür, para birimi, banka, IBAN. */
  function disSec(id: string) {
    setDisId(id);
    const d = (dis ?? []).find((x) => x.disId === id);
    if (!d) return;
    if (!ad.trim()) setAd(d.ad);
    if (!hesap) { setTur(d.tur); if (d.para === 'TRY' || d.para === 'EUR' || d.para === 'USD') setPara(d.para); }
    if (!bankaAdi.trim() && d.bankaAdi) setBankaAdi(d.bankaAdi);
    if (!sube.trim() && d.sube) setSube(d.sube);
    if (!hesapNo.trim() && d.hesapNo) setHesapNo(d.hesapNo);
    if (!iban.trim() && d.iban && ibanGecerli(d.iban)) setIban(ibanYaz(d.iban.replace(/\s+/g, '').toUpperCase()));
    if (!hesap && (acilis ?? 0) === 0) { setAcilis(d.bakiyeKurus); if (!acilisTarihi) setAcilisTarihi(bugunIso()); }
  }

  async function kaydet(e: React.FormEvent) {
    e.preventDefault();
    if (ad.trim().length < 2) { setHata('Hesap adını yazın.'); return; }
    if (ibanKusurlu) { setHata(ibanDurum && !ibanDurum.ok ? `${ibanDurum.neden} Düzeltin ya da IBAN'ı boş bırakın.` : 'IBAN geçersiz.'); return; }
    if (acilis == null) { setHata('Açılış bakiyesi okunamadı. Rakamla yazın (boşsa 0).'); return; }
    if (hesap && hesap.resmi !== resmi && !window.confirm(resmi
      ? `"${hesap.ad}" RESMÎ yapılırsa bütün hareketleri muhasebe rolüne de görünür olur. Emin misiniz?`
      : `"${hesap.ad}" GAYRİRESMÎ yapılırsa yalnız faturasız tarafı görebilenler görür. Emin misiniz?`)) return;
    setHata(null); setBekliyor(true);
    try {
      const govde = {
        ad: ad.trim(), tur, resmi, para,
        bankaAdi: bankaAdi.trim() || null, sube: sube.trim() || null, iban: iban.trim() || null, hesapNo: hesapNo.trim() || null, hesapSahibi: hesapSahibi.trim() || null,
        acilisKurus: acilis, acilisTarihi: acilisTarihi || null, not: not.trim() || null, aktif,
        disHesapId: resmi && bagliProgram ? disId || null : null,
      };
      if (hesap) await api(`/api/kasa/hesap/${hesap.id}`, { method: 'PUT', body: JSON.stringify(govde) });
      else await api('/api/kasa/hesap', { method: 'POST', body: JSON.stringify(govde) });
      kaydedildi(`"${govde.ad}" ${hesap ? 'güncellendi' : 'tanımlandı'}.`);
      kapat();
    } catch (err) { setHata(err instanceof Error ? err.message : 'Hesap kaydedilemedi'); }
    finally { setBekliyor(false); }
  }

  return (
    <Cekmece ust="Tanımlar" baslik={hesap ? 'Hesabı düzenle' : 'Hesap tanımla'} kapat={kapat} mesgul={bekliyor}>
      <form className="ccd-stack" onSubmit={kaydet}>
        <Hata m={hata} />
        {resmi && bagliProgram && (
          <Alan etiket={`${muhasebeAd}'teki hesapla ilişkilendir`} htmlFor="kasa-hs-dis" ipucu={<Ipucu id="mutabakat-farki" />}
                not={disHata ?? (seciliDis ? `${muhasebeAd} bakiyesi ${paraYaz(seciliDis.bakiyeKurus, seciliDis.para)}. Panel bakiyesiyle yan yana gösterilir; ${muhasebeAd}'e hiçbir şey yazılmaz.` : 'İsteğe bağlı. Seçerseniz ad, banka ve IBAN önerilir; iki bakiye yan yana görünür.')}>
            <AraSec id="kasa-hs-dis" secenekler={disSecenek} deger={disId} degis={disSec} yukleniyor={!dis} bosEtiket="— ilişkilendirme yok —" yerTutucu={`${muhasebeAd}'teki hesap adını yazın…`} />
          </Alan>
        )}
        <Alan etiket="Hesap adı" htmlFor="kasa-hs-ad" zorunlu>
          <input id="kasa-hs-ad" className="ccd-input" maxLength={80} value={ad} onChange={(e) => setAd(e.target.value)} placeholder="Örn. Merkez kasa, Garanti TL" />
        </Alan>
        <Alan etiket="Tür">
          <Secim etiket="Tür" deger={tur} degis={setTur} secenekler={[['KASA', 'Kasa'], ['BANKA', 'Banka hesabı'], ['EMANET', 'Emanet hesabı']] as const} />
          {tur === 'EMANET' && <div className="ccd-meta">Emanet hesabı: size ait olmayan paranın ya da elinizdeki çek ve senetlerin durduğu yer (örn. "Eldeki çekler").</div>}
        </Alan>
        {gayriGorur && (
          <Alan etiket="Resmî mi" ipucu={<Ipucu id="resmi-gayriresmi-kasa" />}
                not={resmi ? 'Resmî hesap: muhasebe rolü de görür.' : 'Gayriresmî hesap: yalnız faturasız tarafı görebilenler görür; muhasebe rolüne hiçbir yerde çıkmaz.'}>
            <Secim etiket="Resmî mi" deger={resmi ? 'E' : 'H'} degis={(d) => setResmi(d === 'E')} secenekler={[['E', 'Resmî'], ['H', 'Gayriresmî']] as const} />
          </Alan>
        )}
        <Alan etiket="Para birimi" not={hesap && hesap.hareketAdet > 0 ? 'Bu hesapta hareket var; para birimi değiştirilemez.' : undefined}>
          <Secim etiket="Para birimi" deger={para} degis={setPara} disabled={Boolean(hesap && hesap.hareketAdet > 0)}
                 secenekler={[['TRY', '₺ Türk lirası'], ['EUR', '€ Euro'], ['USD', '$ Dolar']] as const} />
        </Alan>

        {tur === 'BANKA' && (
          <div className="ccd-kasa-icform ccd-stack-sm">
            <div className="ccd-fieldset-title">Banka bilgileri <Ipucu id="banka-hesabi" /></div>
            <Alan etiket="IBAN" htmlFor="kasa-hs-iban"
                  not={ibanDurum === null ? 'IBAN\'ı yazın ya da yapıştırın; banka kendiliğinden tanınır. Boş bırakılabilir.'
                    : ibanDurum.ok ? <span style={{ color: 'var(--ccd-success)' }}>✓ IBAN geçerli{ibanDurum.banka ? ` · ${ibanDurum.banka.ad}` : ' · banka listede yok, adını elle yazın'}</span>
                    : <span style={{ color: 'var(--ccd-danger)' }}>{ibanDurum.neden}</span>}>
              <div className="ccd-field ccd-giris" data-durum={ibanDurum === null ? undefined : ibanDurum.ok ? 'gecerli' : iban.replace(/\s/g, '').length >= 26 || !/^TR\d*$/i.test(iban.replace(/\s/g, '')) ? 'hatali' : undefined}>
                <div className="ccd-giris-kutu">
                  <input id="kasa-hs-iban" className="ccd-input ccd-mono" maxLength={40} value={iban} inputMode="text" autoComplete="off" autoCapitalize="characters" spellCheck={false}
                         aria-invalid={ibanKusurlu} placeholder="TR00 0000 0000 0000 0000 0000 00"
                         onChange={(e) => ibanDegis(e.target.value, e.target.selectionStart === e.target.value.length)} />
                  {ibanDurum?.ok && <span className="ccd-giris-tik" aria-hidden="true">✓</span>}
                </div>
              </div>
            </Alan>
            <div className="ccd-kasa-ikili">
              <Alan etiket="Banka" htmlFor="kasa-hs-banka"
                    not={bankaUyusmuyor ? <span>IBAN {ibanBankasi!.ad} hesabına ait. <button type="button" className="ccd-borc-bag" onClick={() => setBankaAdi(ibanBankasi!.ad)}>{ibanBankasi!.ad} yap</button></span> : undefined}>
                <input id="kasa-hs-banka" className="ccd-input" maxLength={80} list="kasa-hs-banka-liste" autoComplete="off" value={bankaAdi} onChange={(e) => setBankaAdi(e.target.value)} placeholder="Yazdıkça önerir" />
                <datalist id="kasa-hs-banka-liste">{[...BANKALAR].sort((x, y) => x.ad.localeCompare(y.ad, 'tr')).map((x) => <option key={x.kod} value={x.ad} />)}</datalist>
              </Alan>
              <Alan etiket="Şube" htmlFor="kasa-hs-sube"><input id="kasa-hs-sube" className="ccd-input" maxLength={80} value={sube} onChange={(e) => setSube(e.target.value)} placeholder="İsteğe bağlı: şube adı ya da kodu" /></Alan>
            </div>
            <div className="ccd-kasa-ikili">
              <Alan etiket="Hesap no" htmlFor="kasa-hs-no"><input id="kasa-hs-no" className="ccd-input" maxLength={40} value={hesapNo} onChange={(e) => setHesapNo(e.target.value)} /></Alan>
              <Alan etiket="Hesap sahibi" htmlFor="kasa-hs-sahip"><input id="kasa-hs-sahip" className="ccd-input" maxLength={120} value={hesapSahibi} onChange={(e) => setHesapSahibi(e.target.value)} /></Alan>
            </div>
          </div>
        )}

        <div className="ccd-kasa-ikili">
          <Alan etiket="Açılış bakiyesi" htmlFor="kasa-hs-acilis" ipucu={<Ipucu id="acilis-bakiyesi" />} not="Paneli kullanmaya başladığınız gün hesapta olan para.">
            <TutarGiris id="kasa-hs-acilis" kurus={acilis} degis={setAcilis} simge={PARA_SIMGE[para]} eksiOlur />
          </Alan>
          <Alan etiket="Açılış tarihi" htmlFor="kasa-hs-atarih" not="Bundan önceki tarihe hareket yazılamaz.">
            <input id="kasa-hs-atarih" className="ccd-input" type="date" max={bugunIso()} value={acilisTarihi} onChange={(e) => setAcilisTarihi(e.target.value)} />
          </Alan>
        </div>
        <Alan etiket="Not" htmlFor="kasa-hs-not">
          <input id="kasa-hs-not" className="ccd-input" maxLength={500} value={not} onChange={(e) => setNot(e.target.value)} placeholder="İsteğe bağlı" />
        </Alan>
        {hesap && (
          <label className="ccd-kasa-onay">
            <input type="checkbox" checked={aktif} onChange={(e) => setAktif(e.target.checked)} />
            <span><b>Hesap etkin</b> — işareti kaldırırsanız hesap pasife alınır: yeni hareket yazılamaz, geçmişi durur.</span>
          </label>
        )}
        <button className="ccd-btn ccd-btn-primary ccd-btn-lg" disabled={bekliyor}>{bekliyor ? 'Kaydediliyor…' : hesap ? 'Kaydet' : 'Hesabı tanımla'}</button>
        <div className="ccd-meta">Hesap silinmez; kullanılmayan hesap pasife alınır.</div>
      </form>
    </Cekmece>
  );
}

/* ─── Emanet dökümü ──────────────────────────────────────────────────────── */
export function EmanetDokumCekmece({ baslik, ozet, anahtar, kapat }: { baslik: string; ozet: ReactNode; anahtar: string; kapat: () => void }) {
  const [dokum, setDokum] = useState<Hareket[] | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  useEffect(() => {
    api<{ dokum: Hareket[] }>(`/api/kasa/emanet?anahtar=${encodeURIComponent(anahtar)}`).then((r) => setDokum(r.dokum)).catch((e) => setHata((e as Error).message));
  }, [anahtar]);
  return (
    <Cekmece ust="Emanet dökümü" baslik={baslik} kapat={kapat} genis>
      {ozet}
      <Hata m={hata} />
      {!dokum ? !hata && <div className="ccd-inline-note">Yükleniyor…</div> : dokum.length === 0 ? <div className="ccd-empty">Hareket yok.</div> : (
        <div className="ccd-table-wrap ccd-kasa-tablo ccd-kasa-tablo-ic">
          <table className="ccd-table">
            <thead><tr>
              <th className="ccd-th">Tarih</th><th className="ccd-th">İşlem</th><th className="ccd-th">Hesap</th>
              <th className="ccd-th ccd-td-num">Aldığımız</th><th className="ccd-th ccd-td-num">Verdiğimiz</th>
            </tr></thead>
            <tbody>
              {dokum.map((h) => (
                <tr key={h.id} className={`ccd-tr${h.iptal ? ' ccd-kasa-iptal' : ''}`}>
                  <td className="ccd-td ccd-td-nowrap">{gunYaz(h.tarih)}</td>
                  <td className="ccd-td">{h.kategoriAd}{h.aciklama && <div className="ccd-meta">{h.aciklama}</div>}{h.iptal && <div className="ccd-meta">iptal: {h.iptalNeden}</div>}</td>
                  <td className="ccd-td">{h.hesapAd}</td>
                  <td className="ccd-td ccd-td-num">{h.yon === 'GIRIS' ? paraYaz(h.tutarKurus, h.para) : ''}</td>
                  <td className="ccd-td ccd-td-num">{h.yon === 'CIKIS' ? paraYaz(h.tutarKurus, h.para) : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Cekmece>
  );
}
